'use client';

import { c as _c } from "react/compiler-runtime";
import './content-feed-card.scss';
import React from 'react';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { ContentFeedAuthor, ContentFeedFundingProgress, ContentFeedMedia, ContentFeedQueryMatch } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { ContentFeedAvatar } from '../../atoms/content-feed-avatar/content-feed-avatar.tsx';
import { ContentFeedProgressBar } from '../../atoms/content-feed-progress-bar/content-feed-progress-bar.tsx';
import { ContentFeedGallery } from '../../molecules/content-feed-gallery/content-feed-gallery.tsx';
import { useComponentContentFeedCard } from './content-feed-card.logic.ts';
export type { ContentFeedAuthor, ContentFeedFundingProgress, ContentFeedMedia } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
export type ContentFeedMatch = ContentFeedQueryMatch;
export interface ContentFeedCardProps {
  /** Content source type — determines which layout variant to render */
  sourceType?: 'causes' | 'community' | 'compassion.media';
  /** Icon displayed in the top banner */
  icon?: IconNames;
  /** Entry title (resolved for locale) */
  title: string;
  /** Entry description (resolved for locale) — used by community/compassion.media sources */
  description?: string;
  /** Media assets from the entry */
  media?: ContentFeedMedia[];
  /** Funding progress metadata (Causes source only) */
  fundingProgress?: ContentFeedFundingProgress;
  /** Link metadata for the CTA */
  link?: {
    url: string;
  };
  /** Program country ISO code (e.g. "DO", "UG") */
  programCountryIsoCode?: string;
  /** Resolved country display name (looked up from isoCode) */
  programCountryName?: string;
  /** Query match context — drives the banner relationship message */
  match?: ContentFeedMatch;
  /** Custom banner text override. If not provided, derived from match data. */
  bannerText?: string;
  /** Whether to show the "New Update" badge on the first image */
  hasNewUpdate?: boolean;
  /** CTA button label override */
  ctaLabel?: string;
  /** ISO 8601 timestamp of when the entry was published */
  publishedAt?: string;
  /** Author of the entry content (community/compassion.media sources) */
  author?: ContentFeedAuthor;
}
export const ContentFeedCard: React.FC<ContentFeedCardProps> = props => {
  const $ = _c(34);
  const {
    isCausesLayout,
    isPostLayout,
    author,
    authorLine,
    formattedPublishedAt,
    description,
    icon,
    title,
    fundingProgress,
    link,
    programCountryName,
    hasNewUpdate,
    galleryItems,
    totalPhotos,
    resolvedBannerText,
    progressPercent,
    formattedRaised,
    formattedGoal,
    fundedMessage,
    lightboxItems,
    resolvedCtaLabel,
    t
  } = useComponentContentFeedCard(props);
  let t0;
  if ($[0] !== icon || $[1] !== resolvedBannerText) {
    t0 = resolvedBannerText && <div className="ContentFeedCard__banner flex items-center gap-snug-xl p-comfy-md"><ArkIcon iconName={icon} className="ContentFeedCard__banner-icon label-neutral-mild" size="1.5em" /><span className="label-moderate label-md label-neutral-moderate">{resolvedBannerText}</span></div>;
    $[0] = icon;
    $[1] = resolvedBannerText;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== author || $[4] !== authorLine || $[5] !== formattedPublishedAt || $[6] !== isPostLayout || $[7] !== programCountryName) {
    t1 = isPostLayout && author && <div className="ContentFeedCard__post-header"><div className="flex items-start gap-comfy-sm"><ContentFeedAvatar src={author.photo?.url} alt={author.displayName} /><div className="flex flex-col"><span className="label-potent label-md label-neutral-moderate mb-snug-sm">{authorLine}</span>{programCountryName && <div className="flex items-center gap-snug-sm label-neutral-mild"><ArkIcon iconName="marker-pin-01" size="1em" /><span className="label-sm">{programCountryName}</span></div>}</div></div>{formattedPublishedAt && <span className="label-sm label-neutral-mild flex-shrink-0">{formattedPublishedAt}</span>}</div>;
    $[3] = author;
    $[4] = authorLine;
    $[5] = formattedPublishedAt;
    $[6] = isPostLayout;
    $[7] = programCountryName;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== galleryItems || $[10] !== hasNewUpdate || $[11] !== lightboxItems || $[12] !== t || $[13] !== title || $[14] !== totalPhotos) {
    t2 = <ContentFeedGallery galleryItems={galleryItems} lightboxItems={lightboxItems} totalPhotos={totalPhotos} hasNewUpdate={hasNewUpdate} title={title} t={t} />;
    $[9] = galleryItems;
    $[10] = hasNewUpdate;
    $[11] = lightboxItems;
    $[12] = t;
    $[13] = title;
    $[14] = totalPhotos;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== description || $[17] !== formattedGoal || $[18] !== formattedRaised || $[19] !== fundedMessage || $[20] !== fundingProgress || $[21] !== isCausesLayout || $[22] !== link || $[23] !== programCountryName || $[24] !== progressPercent || $[25] !== resolvedCtaLabel || $[26] !== t || $[27] !== title) {
    t3 = isCausesLayout ? <div className="flex flex-col gap-comfy-sm p-comfy-lg pt-0">{programCountryName && <div className="flex items-center gap-snug-sm label-neutral-mild"><ArkIcon iconName="marker-pin-01" size="1.25em" /><span className="label-moderate label-md">{programCountryName}</span></div>}<h3 className="label-lg label-potent">{title}</h3>{fundedMessage ? <p className="label-md label-neutral-mild">{fundedMessage}</p> : fundingProgress && <div className="flex flex-col gap-snug-lg"><ContentFeedProgressBar percent={progressPercent} ariaLabel={t("my:contentFeed.progressLabelA11y", {
          raised: formattedRaised,
          goal: formattedGoal
        })} /><p className="label-moderate label-md label-neutral-mild"><IntlBlock i18nKey="my:contentFeed.progressLabel" values={{
            raised: formattedRaised,
            goal: formattedGoal
          }} /></p></div>}{link?.url && <div className="pt-comfy-sm"><ButtonStyle variant="primary" size="lg" fullWidth={true}><Link href={link.url}>{resolvedCtaLabel}</Link></ButtonStyle></div>}</div> : description && <div className="flex flex-col gap-comfy-sm p-comfy-lg pt-0"><p className="label-md label-neutral-moderate ContentFeedCard__description">{description}</p></div>;
    $[16] = description;
    $[17] = formattedGoal;
    $[18] = formattedRaised;
    $[19] = fundedMessage;
    $[20] = fundingProgress;
    $[21] = isCausesLayout;
    $[22] = link;
    $[23] = programCountryName;
    $[24] = progressPercent;
    $[25] = resolvedCtaLabel;
    $[26] = t;
    $[27] = title;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  let t4;
  if ($[29] !== t0 || $[30] !== t1 || $[31] !== t2 || $[32] !== t3) {
    t4 = <article className="ContentFeedCard rounded-lg overflow-hidden">{t0}{t1}{t2}{t3}</article>;
    $[29] = t0;
    $[30] = t1;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  return t4;
};